import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, MenuItem, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addPopulerCity, editPopulerCity, getPopulerCityById } from '../../../../api/endpoints/cmsContent';
import type { City, PopulerCity } from '../../../../api/types/cmsContent';
import { useFreshEditRow } from '../../../../components/useFreshEditRow';
import { FormDialog } from '../../../../components/common';

const schema = z.object({
  title: z.string().min(1, 'Başlık gerekli'),
  cityCode: z.string().optional(),
  link: z.string().optional(),
  type: z.string().optional(),
  priority: z.coerce.number().optional(),
  price: z.coerce.number().optional(),
  priceCurrency: z.string().optional(),
  city_Id: z.coerce.number().min(1, 'Şehir seçimi gerekli'),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  title: '',
  cityCode: '',
  link: '',
  type: 'DOM',
  priority: 0,
  price: 0,
  priceCurrency: 'TRY',
  city_Id: undefined as unknown as number,
};

function toFormValues(row: PopulerCity): FormValues {
  return {
    title: row.title ?? '',
    cityCode: row.cityCode ?? '',
    link: row.link ?? '',
    type: row.type ?? 'DOM',
    priority: row.priority ?? 0,
    price: row.price ?? 0,
    priceCurrency: row.priceCurrency ?? 'TRY',
    city_Id: row.city_Id ?? (undefined as unknown as number),
  };
}

/** Create/edit dialog for Popüler Şehirler (PopulerCity). Confirmed via local-backend
 *  swagger + full live curl round-trip (see api/types/cmsContent.ts header). `cities` is the
 *  current site's City list loaded by the list page, used to populate the "Şehir" picker —
 *  a plain MUI `select TextField` fed with `{id,label}`-style options, matching this
 *  codebase's existing FK-select precedent (see
 *  modules/corporate/department/DepartmentFormDialog.tsx's "Üst Departman" picker). Always
 *  rendered inside a specific site's tab, so `site_Id` is injected from the `siteId` prop. */
export function PopulerCityFormDialog({
  open,
  populerCity,
  cities,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  populerCity: PopulerCity | null;
  cities: City[];
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!populerCity;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error.
  const seed = useFreshEditRow({
    open,
    editing: populerCity,
    queryKey: ['cms', 'content', 'populerCity', populerCity?.id],
    fetch: async (row) => (await getPopulerCityById(row.id)).populerCities?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && populerCity
        ? editPopulerCity({ id: populerCity.id, site_Id: siteId, ...values })
        : addPopulerCity({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Popüler şehir güncellendi.' : 'Popüler şehir oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'populerCities'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Popüler Şehri Güncelle' : 'Yeni Popüler Şehir'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Başlık"
                error={!!errors.title}
                helperText={errors.title?.message}
                {...register('title')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="city_Id"
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    select
                    fullWidth
                    size="small"
                    label="Şehir"
                    error={!!errors.city_Id}
                    helperText={errors.city_Id?.message}
                  >
                    {cities.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name ?? c.displayName ?? `#${c.id}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Şehir Kodu" {...register('cityCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Link" {...register('link')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Tip">
                    <MenuItem value="DOM">DOM</MenuItem>
                    <MenuItem value="INT">INT</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" type="number" label="Öncelik" {...register('priority')} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" type="number" label="Fiyat" {...register('price')} />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField fullWidth size="small" label="Para Birimi" {...register('priceCurrency')} />
            </Grid>
          </Grid>
    </FormDialog>
  );
}
